/// <amd-module name="Wit/LinkedWorkItemsDropdown/Util/Artifact" />
import { format } from "VSS/Core/Util/String";
import { encodeUri, IArtifactData } from "VSS/Platform/Util/Linking";

export interface IArtifactLink {
    name: string;
    uri: string;
}

export interface IArtifactLinkingProps {
    artifactChangesContext: IArtifactChangesContext;
}

export interface IArtifactChangesContext {
    addArtifactCompleteListener: (listener: ArtifactCompleteListener) => void;
}

export declare type ArtifactCompleteListener = (artifactLinks: IArtifactLink[]) => void | Promise<void>;

export const ArtifactLinkNames = {
    ChangesetToWorkItem: "Fixed in Changeset",
    CommitToWorkItem: "Fixed in Commit",
    BranchToWorkItem: "Branch",
    PullRequestToWorkItem: "Pull Request"
};

const ArtifactIdFormat = "{0}/{1}/{2}";
const ArtifactGitToolName = "Git";
const ArtifactTfvcToolName = "VersionControl";

export function getCommitArtifact(projectId: string, repositoryId: string, commitId: string): IArtifactData {
    return {
        id: format(ArtifactIdFormat, projectId, repositoryId, commitId),
        tool: ArtifactGitToolName,
        type: "Commit"
    };
}

export function getChangesetArtifact(changesetId: number): IArtifactData {
    return {
        id: changesetId.toString(),
        tool: ArtifactTfvcToolName,
        type: "Changeset"
    };
}

export function getChangesetArtifactLink(changesetId: number): IArtifactLink {
    return getArtifactLink(ArtifactLinkNames.ChangesetToWorkItem, getChangesetArtifact(changesetId));
}

export function getCommitArtifactLink(projectId: string, repositoryId: string, commitId: string, artifactName: string): IArtifactLink {
    return getArtifactLink(artifactName, getCommitArtifact(projectId, repositoryId, commitId));
}

export function getBranchArtifact(projectId: string, repositoryId: string, branchName: string): IArtifactData {
    return getRefArtifact(projectId, repositoryId, `GB${branchName}`);
}

export function getBranchArtifactLink(projectId: string, repositoryId: string, branchName: string, artifactName: string): IArtifactLink {
    return getArtifactLink(artifactName, getBranchArtifact(projectId, repositoryId, branchName));
}

export function getPullRequestArtifactLink(projectId: string, repositoryId: string, pullRequestId: number): IArtifactLink {
    return getArtifactLink(ArtifactLinkNames.PullRequestToWorkItem, {
        id: format(ArtifactIdFormat, projectId, repositoryId, pullRequestId),
        tool: ArtifactGitToolName,
        type: "PullRequestId"
    });
}

function getRefArtifact(projectId: string, repositoryId: string, refVersion: string): IArtifactData {
    return {
        id: format(ArtifactIdFormat, projectId, repositoryId, refVersion),
        tool: ArtifactGitToolName,
        type: "Ref"
    };
}

function getArtifactLink(artifactName: string, artifactData: IArtifactData): IArtifactLink {
    return {
        name: artifactName,
        uri: encodeUri(artifactData)
    };
}
